import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { channelOperations } from '../../../../models/channels';
import {
  trackChannelFollow,
  trackChannelUnfollow,
} from '../../../../models/channels/analytics';
import { Button, Icon } from '@socialchorus/shared-ui-components';
import { useFollowChannelButtonState } from '../../../../common/channel/use-follow-channel-button-state';
import { ChannelButtonTooltip } from '../../../channel/channel-button-tooltip';

type ChannelFollowButtonProps = {
  isFollowing: boolean;
  followingText: string;
  notFollowingText: string;
  handleClick: () => void;
};

const ChannelFollowButton = ({
  isFollowing,
  followingText,
  notFollowingText,
  handleClick,
}: ChannelFollowButtonProps) => {
  const label = isFollowing ? followingText : notFollowingText;

  return (
    <Button
      variant={isFollowing ? 'outline' : 'primary'}
      size="compact"
      label={label}
      leftIcon={<Icon>{isFollowing ? 'done' : 'add'}</Icon>}
      onClick={handleClick}
      aria-label={label}
      style={{ zIndex: 1 }} //safari fix, make sure this appears on top of shadow overflow padding on .content-layout__horizontal-scroll
    />
  );
};

type ChannelFollowButtonContainerProps = {
  channelId: string;
  analyticsData: unknown;
};

const ChannelFollowButtonContainer = ({
  channelId,
  analyticsData,
}: ChannelFollowButtonContainerProps) => {
  const dispatch = useDispatch();
  const { label, disabled, following, aria } = useFollowChannelButtonState({
    channelId,
  });

  const handleClick = useCallback(() => {
    if (following) {
      dispatch(channelOperations.unfollowChannel(channelId));
      trackChannelUnfollow(channelId, analyticsData);
    } else {
      dispatch(channelOperations.followChannel(channelId));
      trackChannelFollow(channelId, analyticsData);
    }
  }, [dispatch, channelId, analyticsData, following]);

  return (
    <ChannelButtonTooltip channelId={channelId}>
      <Button
        variant={following ? 'outline' : 'primary'}
        size="compact"
        label={label}
        leftIcon={<Icon>{following ? 'done' : 'add'}</Icon>}
        disabled={disabled}
        onClick={handleClick}
        style={{ zIndex: 1 }}
        {...aria}
      />
    </ChannelButtonTooltip>
  );
};

export default ChannelFollowButtonContainer;
